import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { incrementRouterSpaceReactKey } from '~src/app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import {
  ModifiedAtDetailSection,
  ModifiedByDetailSection
} from '~src/app/content/contentDetailDialog/CommonContentDetailSections';
import { sectionContainer } from '~src/app/content/contentDetailDialog/ContentDetailSection';
import { ConnectedDeleteContentSection } from '~src/app/content/contentDetailDialog/DeleteContentSection';
import {
  DESCRIPTION_MAX_LENGTH,
  DescriptionDetailSection
} from '~src/app/content/contentDetailDialog/DescriptionDetailSection';
import { ConnectedOwnerDetailSection } from '~src/app/content/contentDetailDialog/OwnerDetailSection';
import { ProjectDetailSection } from '~src/app/content/contentDetailDialog/ProjectDetailSection';
import { TagsDetailSection } from '~src/app/content/contentDetailDialog/TagsDetailSection';
import { CompositeSessionInfoSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { ServiceLocator } from '~src/app/ServiceLocator';
import { AppState } from '~src/app/state/AppState';
import { MapDispatchToProps, MapStateToProps } from '~src/app/utils/ConnectUtils';
import { Messages } from '~src/codegen/Localize';
import { ContentActionsService } from '~src/commonInfra/contentActions/ContentActions';
import { DataSources } from '~src/content/resources/Datasources';
import { ChangeDatasourceAskDataAction } from '~src/content/services/ChangeDatasourceAskDataAction';
import { ChangeDatasourceDescriptionAction } from '~src/content/services/ChangeDatasourceDescriptionAction';
import { ChangeTagsActionHelperService } from '~src/content/services/ChangeTagsActionHelper';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { RevisionHistoryActionInstance } from '~src/revisionHistory/services/RevisionHistoryAction';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { AskDataIndexing, IDataSourceDetailInfoWithUsage, NlpSetting } from '~src/ts/models/IDataSourceInfo';
import { Set } from '~src/ts/models/Set';
import { ISetDatasourceCertificationParams } from '~src/ts/ServerApi/requests/SetDatasourceCertificationRequest';
import { ISetDatasourceNlpSettingParams } from '~src/ts/ServerApi/requests/SetDatasourceNlpSettingRequest';
import { IDatasourceActions } from '~src/ts/ServerApi/types/IDatasourceActions';

import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedAskDataSettingsSection } from './AskData/AskDataSettingsSection';
import { DataCertificationSection } from './DataCertificationSection';
import { ConnectedLegacyAskDataSettingsSection } from './LegacyAskDataSettingsSection';
import { RemoteQueryAgentDetailSection } from './RemoteQueryAgentDetailSection';


interface DatasourceDetailsDialogBodyOwnProps {
  readonly datasource: IDataSourceDetailInfoWithUsage;
  readonly allowedActions: IDatasourceActions;
}

interface DatasourceDetailsDialogBodyStateProps {
  readonly isAdminOrOwner: boolean;
  readonly urls: SpacesSiteUrls;
}

interface DatasourceDetailsDialogBodyDispatchProps {
  readonly incrementRouterSpaceReactKey: () => void;
}

export type DatasourceDetailsDialogBodyProps =
    DatasourceDetailsDialogBodyOwnProps &
    DatasourceDetailsDialogBodyStateProps &
    DatasourceDetailsDialogBodyDispatchProps;

export function DatasourceDetailsDialogBody(props: DatasourceDetailsDialogBodyProps): JSX.Element {
  const { datasource, allowedActions, isAdminOrOwner, urls, incrementRouterSpaceReactKey } = props;

  function allowedAction(actionName: keyof IDatasourceActions): boolean {
    return !!(allowedActions?.[actionName]);
  }

  const setDescription = React.useCallback((description: string) => {
    return ChangeDatasourceDescriptionAction.execute(datasource, description);
  }, [datasource]);

  const moveToProject = React.useCallback(() => {
    return ContentActionsService.moveDatasourcesToProject(Set.singletonForIdItem(datasource));
  }, [datasource]);

  const setTags = React.useCallback((tags: string[]) => {
    return ChangeTagsActionHelperService.changeTagsForContent([datasource], datasource.tags, tags)
        .then((results: IResult[]) => ResultHelper.combineResults(results));
  }, [datasource]);

  const showRevisionHistory = React.useCallback(() => {
    return RevisionHistoryActionInstance.showDataSourceRevisionHistory(datasource);
  }, [datasource]);

  const setNlpSettings = React.useCallback((nlpOption: NlpSetting, feedbackSetting: boolean) => {
    const params: ISetDatasourceNlpSettingParams = {
      id: datasource.id,
      nlpOption,
    };
    if (FeatureFlags.nlFeedbackToCuratorEnabled) {
      params.askDataFeedbackToCuratorEnabled = feedbackSetting;
    }

    return DataSources.setNlpSetting(params);
  }, [datasource]);

  const setAskDataSettings = React.useCallback((indexing: AskDataIndexing, feedbackToCurator: boolean) => {
    return ChangeDatasourceAskDataAction.execute(datasource, indexing, feedbackToCurator);
  }, [datasource]);

  const setCertification = React.useCallback((isCertified: boolean, certificationNote: string) => {
    const params: ISetDatasourceCertificationParams = {
      id: datasource.id,
      isCertified,
      certificationNote,
    };

    return DataSources.setCertification(params);
  }, [datasource]);

  const allowRemoteQueryAgentDetailSection =
    FeatureFlags.bridgeLiveDirectAssignEnabled &&
    !props.datasource.hasExtracts &&
    !props.datasource.testingConnectionAllowed;

  return (
    <div className={sectionContainer}>
      <DescriptionDetailSection
        description={datasource.description}
        isActionEnabled={allowedAction('setDescription')}
        setDescription={setDescription}
        descriptionMaxLength={DESCRIPTION_MAX_LENGTH}
        showPreviewAndFormattingHints={true}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      <ProjectDetailSection
        project={datasource.project}
        isActionEnabled={allowedAction('setProject')}
        moveToProject={moveToProject}
        urls={urls}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      <ConnectedOwnerDetailSection content={datasource} isActionEnabled={allowedAction('setOwner')} urls={urls}/>
      <TagsDetailSection
        tags={datasource.tags}
        isActionEnabled={allowedAction('setTags')}
        setTags={setTags}
        tagUrlFn={(tag: string) => (urls.datasourcesWithTagUrl(tag))}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      <ModifiedAtDetailSection
        date={datasource.updatedAt}
        allowShowRevisionHistory={FeatureFlags.detailsDialogsRevisionHistory && allowedAction('versionManagement')}
        showRevisionHistory={showRevisionHistory}
      />
      {/*DEBUG SECTION ONLY*/
        allowRemoteQueryAgentDetailSection &&
          <RemoteQueryAgentDetailSection datasource={datasource} />
      }
      {!FeatureFlags.nlplensContentTypeEnabled &&
        <ConnectedLegacyAskDataSettingsSection
          isAdminOrOwner={isAdminOrOwner}
          setNlpSettings={setNlpSettings}
          incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
        />
      }
      {FeatureFlags.nlplensContentTypeEnabled &&
        <ConnectedAskDataSettingsSection
          isAdminOrOwner={isAdminOrOwner}
          setAskDataSettings={setAskDataSettings}
          incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
        />
      }
      {FeatureFlags.datasourceCertificationEnabled &&
        <DataCertificationSection
          isCertified={datasource.certificationDetails.isCertified}
          certifier={datasource.certificationDetails.certifier}
          note={datasource.certificationDetails.certificationNote}
          isActionEnabled={allowedAction('setCertification')}
          urls={urls}
          setCertification={setCertification}
          incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
        />
      }
      {FeatureFlags.modifiedByUserEnabled &&
        <ModifiedByDetailSection user={datasource.modifiedBy} urls={urls} />
      }
      {allowedAction('del') &&
        <ConnectedDeleteContentSection
          actionMessage={Messages.deleteDataSource_action()}
          content={datasource}
        />
      }
    </div>
  );
}

const mapDispatchToProps: MapDispatchToProps<DatasourceDetailsDialogBodyDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey: incrementRouterSpaceReactKey
}, dispatch);

const mapStateToProps: MapStateToProps<DatasourceDetailsDialogBodyStateProps> = (state: AppState, ownProps: DatasourceDetailsDialogBodyOwnProps) => ({
  isAdminOrOwner: CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(ownProps.datasource.owner)(state),
  urls: siteUrlsSelector(state),
});

export const ConnectedDatasourceDetailsDialogBody: React.ComponentClass<DatasourceDetailsDialogBodyOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(DatasourceDetailsDialogBody);
